import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Briefcase,
  CalendarClock,
  CheckCircle,
  Clock,
  DollarSign,
  FileClock,
  Gift,
  TrendingUp,
} from "lucide-react";

import {
  fetchDealDashboard,
  fetchMyProfile,
  type DealDashboardMetrics,
  type Profile,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { StatCard } from "@/components/StatCard";
import { dealTabCount, type DealTab } from "@/components/deals/dealTabs";
import { FormError } from "@/components/ui/form-error";
import { formatAud } from "@/lib/currency";
import { deriveConceptualRole } from "@/lib/role-labels";
import { SPIF_FLAT_RATE_CENTS, spifPayoutType } from "@/lib/spif";

export interface DealDashboardMetricsSectionProps {
  /**
   * Partner scorecard cards are clickable — clicking one selects the matching
   * workspace tab. CHP-370: "Total Sales Closed YTD" additionally requests a
   * current-year close-date filter via `opts.closedThisYear`. CHP-1038: "My
   * expiring deals" requests a soonest-expiry-first ledger sort via
   * `opts.expiringSoonest`. Omit on the CM/RM/RA view (non-interactive).
   */
  onCardClick?: (
    tab: DealTab,
    opts?: { closedThisYear?: boolean; expiringSoonest?: boolean },
  ) => void;
  /**
   * CHP-798: count of the partner's live deals expiring in the next 30 days,
   * derived FE-side by the partner route from its own `listMyDeals` data (the
   * dashboard metrics endpoint has no partner-scoped expiring figure). Drives
   * the partner-only "Deals expiring" scorecard; omit on the CM/RM/RA view.
   */
  expiringCount?: number;
}

export function DealDashboardMetricsSection({
  onCardClick,
  expiringCount = 0,
}: DealDashboardMetricsSectionProps = {}) {
  const { accessToken, data: user } = useCurrentUser();

  const dashboard = useQuery<DealDashboardMetrics>({
    queryKey: ["deals", "dashboard", accessToken],
    queryFn: () => fetchDealDashboard(accessToken!),
    enabled: !!accessToken,
  });

  // The SPIF metric module's label/visibility switches on the viewer's
  // payout setup (gap-build W2). Same key as the settings page — shared cache.
  const profile = useQuery<Profile | null>({
    queryKey: ["profiles", "me"],
    queryFn: () => fetchMyProfile(accessToken!),
    enabled: !!accessToken,
  });

  if (dashboard.isLoading) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading metrics...
      </p>
    );
  }

  if (dashboard.error) {
    return <FormError>Could not load dashboard metrics.</FormError>;
  }

  if (!dashboard.data) return null;

  const d = dashboard.data;

  const conceptualRole = user
    ? deriveConceptualRole({
        role: user.role,
        is_org_admin: user.is_org_admin,
        is_resetdata_master: user.is_resetdata_master,
      })
    : null;

  const isPartner = conceptualRole === "bdm" || conceptualRole === "po";

  if (isPartner) {
    // Three clickable scorecards (dashboard plan): each routes to the
    // matching workspace tab. CHP-1039: the Live tab holds only `live` deals
    // since CHP-797 moved extension-pending into In-review, so the pipeline
    // count is active_deals alone — adding extension_pending double-counted
    // those deals across this card and the In-review card.
    const activePipelineCount = d.active_deals;

    // CHP-1037: mirror the In-review tab's status grouping (pending_review +
    // win_pending_review + extension_pending) so the card equals the tab badge.
    const inReviewCount = dealTabCount(d, "pending");

    // SPIF metric module (gap-build W2). Counts × the flat rate; visibility
    // switches on the firm's payout setup from the viewer's profile — only
    // direct payout shows the BDM-facing pending/paid card. Pooled payout
    // renders nothing (the "Value Generated for Company" card was removed for
    // PO/BDM — CHP-1011), as does no payout setup (org-less partners, or an
    // org whose PO hasn't elected — CHP-1175 resolves the value from the
    // Organisation, so BDMs now see their firm's rule too).
    const payout = spifPayoutType(profile.data);
    const spifPendingValue = d.spif_pending_count * SPIF_FLAT_RATE_CENTS;
    const spifPaidValue = d.spif_paid_count * SPIF_FLAT_RATE_CENTS;
    const spifCard =
      payout === "direct" ? (
        <StatCard
          title="AUD SPIF Pending / Paid"
          value={formatAud(spifPendingValue)}
          subtitle={`Paid: ${formatAud(spifPaidValue)}`}
          icon={Gift}
          testId="deals-spif"
          tone="success"
          onClick={onCardClick ? () => onCardClick("won") : undefined}
        />
      ) : null;

    // CHP-799: partner scorecard order — Deals expiring (CHP-798), then Deals
    // in review, Active pipeline, Total sales closed YTD; the optional SPIF card
    // follows these four. The grid fits four across (the SPIF card wraps to a
    // second row when present).
    return (
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* CHP-798: live deals expiring within the next 30 days (FE-derived by
            the route). Amber-edge attention metric, mirroring the CM/RA/RM
            "Expiring soon" tile. */}
        <StatCard
          title="My expiring deals"
          value={expiringCount}
          subtitle="# in next 30 days"
          icon={CalendarClock}
          testId="deals-expiring"
          accent="amber"
          empty={expiringCount === 0}
          emptyHint="None expiring soon"
          // CHP-1038: land the Live ledger soonest-expiry-first so the deals
          // this card counts surface at the top.
          onClick={onCardClick ? () => onCardClick("active", { expiringSoonest: true }) : undefined}
        />
        <StatCard
          title="Deals in review"
          value={inReviewCount}
          icon={FileClock}
          testId="deals-pending"
          empty={inReviewCount === 0}
          emptyHint="No deals in review"
          onClick={onCardClick ? () => onCardClick("pending") : undefined}
        />
        <StatCard
          title="My active pipeline"
          value={formatAud(d.active_deals_value)}
          subtitle={`${activePipelineCount} live deal${activePipelineCount === 1 ? "" : "s"}`}
          icon={DollarSign}
          testId="deals-active"
          empty={activePipelineCount === 0}
          emptyHint="No live deals yet"
          onClick={onCardClick ? () => onCardClick("active") : undefined}
        />
        <StatCard
          title="My Total Sales Closed YTD"
          value={formatAud(d.total_closed_won_value)}
          subtitle={`${d.won_deals} deal${d.won_deals === 1 ? "" : "s"} won`}
          icon={TrendingUp}
          testId="deals-total-closed"
          // Genuine positive achievement — teal numeral.
          tone="success"
          empty={d.won_deals === 0}
          emptyHint="No closed deals yet"
          // CHP-370: land on the Closed-won tab with a current-year close-date
          // filter so the ledger matches the YTD headline.
          onClick={onCardClick ? () => onCardClick("won", { closedThisYear: true }) : undefined}
        />
        {spifCard}
      </div>
    );
  }

  // CM / RM / RA view
  return (
    <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title="In review"
        value={d.pending_review}
        icon={Clock}
        tone="neutral"
        testId="deals-pending-reviews"
        empty={d.pending_review === 0}
        emptyHint="No deals in review"
      />
      <StatCard
        title="Active deals"
        value={d.active_deals}
        icon={Briefcase}
        tone="neutral"
        testId="deals-active"
        empty={d.active_deals === 0}
        emptyHint="No live deals yet"
      />
      <StatCard
        title="Win in review"
        value={d.win_pending_review}
        icon={Clock}
        tone="neutral"
        testId="deals-win-pending"
        empty={d.win_pending_review === 0}
        emptyHint="No wins in review"
      />
      {/* Deals won — genuine positive achievement. */}
      <StatCard
        title="Deals won"
        value={d.won_deals}
        icon={CheckCircle}
        tone="success"
        testId="deals-won"
        empty={d.won_deals === 0}
        emptyHint="No deals won yet"
      />
      {/* Total sales closed — genuine positive achievement. */}
      <StatCard
        title="Total sales closed"
        value={formatAud(d.total_closed_won_value)}
        icon={DollarSign}
        tone="success"
        testId="deals-total-closed"
        empty={d.total_closed_won_value === 0}
        emptyHint="No closed deals yet"
      />
      <StatCard
        title="Pipeline value"
        value={formatAud(d.active_deals_value)}
        icon={DollarSign}
        tone="neutral"
        testId="deals-pipeline"
        empty={d.active_deals_value === 0}
        emptyHint="No live deals yet"
      />
      {d.extension_pending > 0 && (
        <StatCard
          title="Extension requests"
          value={d.extension_pending}
          icon={Clock}
          tone="neutral"
          testId="deals-extension-pending"
        />
      )}
      {d.expiring_soon > 0 && (
        // Attention metric — colour by exception on the left edge, not a tint.
        <StatCard
          title="Expiring soon"
          value={d.expiring_soon}
          icon={AlertTriangle}
          tone="neutral"
          accent="amber"
          testId="deals-expiring"
        />
      )}
    </div>
  );
}
